import { DeviceDataPointViewModel, DeviceDataIdentifier, DeviceDataViewModel, DeviceDataViewModelProfile3D, DeviceDataViewModelImage, Point3DViewModel } from "@/TemsManagerWebInterface";
import SiteManagerView from "@/Site/SiteManagerView";
import TemsWebEvents from "@/TemsWebEvents";
import SiteView from "./SiteView";
import Graph2D, { ScatterDataMode } from "@/Graph2D";
import WrapPanel from "./WrapPanel"
import { SurfaceMaterial } from "@/Three/ThreeView";
import { TemsObject3D, Point3D } from "@/CommonTypes";
import THREE, { Vector3 } from "three";

interface ActiveDataPoint {
    DeviceId: string;
    DataPointId: string;
    DataFormat: string;
    IsCompressed: boolean;
    Color: THREE.Color;
    MeshId: string;
    RoadId: string;
    SeriesId: string;
    SeriesName: string;
    DataPointViewModel: DeviceDataPointViewModel;
}

export default class DataStreamManagerObj {

    private static graphFormats = ["Ectn.Utilities.Profile2D", "Ectn.Utilities.Profile3D", "Ectn.Utilities.ProfilePolar3D"];
    private static imageFormats = ["Ectn.Utilities.ByteBitmap", "Ectn.Utilities.RgbBitmap"];

    private colorPalette: THREE.Color[] = [
        new THREE.Color("firebrick"),
        new THREE.Color("royalblue"),
        new THREE.Color("goldenrod"),
        new THREE.Color("yellowgreen"),
        new THREE.Color("lightsalmon"),
        new THREE.Color("deepskyblue"),
        new THREE.Color("forestgreen"),
        new THREE.Color("orchid")
    ];

    private dataPoints: ActiveDataPoint[] = [];

    private siteManagerView: SiteManagerView;
    private siteView: SiteView;
    private attachedGraphs: Graph2D[];
    private imageWrapPanel: WrapPanel;

    public get OnlyGraphDataPoints(): ActiveDataPoint[] {
        return this.dataPoints.filter(this.isGraphFormat);
    }

    public get OnlyImageDataPoints(): ActiveDataPoint[] {
        return this.dataPoints.filter(this.isImageFormat);
    }

    constructor(siteManagerView: SiteManagerView, siteView: SiteView, profiles2DGraph: Graph2D) {
        this.siteManagerView = siteManagerView;
        this.siteView = siteView;
        this.attachedGraphs = [profiles2DGraph];
        this.resetGraphDataTimer();
        this.resetImageDataTimer();

        TemsWebEvents.subscribeEvent(TemsWebEvents.ToggleDataStream, (evt, devcieDataPointViewModel: DeviceDataPointViewModel) => this.toggleDataStream(devcieDataPointViewModel));
        TemsWebEvents.subscribeEvent(TemsWebEvents.SiteStateChanged, (evt) => this.disableAllDataPoints());

        this.imageWrapPanel = new WrapPanel($("#liveImageContainers"));
    }

    public attachGraph(graph: Graph2D) {
        var idx = this.attachedGraphs.indexOf(graph);
        if (idx < 0) {
            this.attachedGraphs.push(graph);
        }
    }

    public detachGraph(graph: Graph2D) {
        var idx = this.attachedGraphs.indexOf(graph);
        if (idx > -1) {
            this.attachedGraphs.splice(idx, 1);
        }
    }

    private disableAllDataPoints() {
        while (this.dataPoints.length > 0) {
            this.disableDataPoint(0);
        }
    }

    private toggleDataStream(deviceDataPointViewModel: DeviceDataPointViewModel): void {
        let deviceId = deviceDataPointViewModel.DeviceConfigId,
            dataPointId = deviceDataPointViewModel.DataPointId,
            dataFormat = deviceDataPointViewModel.DataFormat,
            isCompressed = deviceDataPointViewModel.IsCompressed;

        const dpIdx = this.findActiveDataPointIndex(deviceId, dataPointId);
        if (dpIdx === -1) { // not enabled yet
            let road = this.siteManagerView.findRoadIdByDeviceId(deviceId.replace(/-/g, ""));
            let device = this.siteManagerView.getObjectFromSiteById(deviceId.replace(/-/g, "")); //, road.Devices);

            if (this.colorPalette.length === 0) {
                // we need to first disable a datapoint!
                this.disableDataPoint(0);
            }

            const color = this.colorPalette.splice(0, 1)[0];
            var activeDataPoint = {
                DeviceId: deviceId,
                DataPointId: dataPointId,
                DataFormat: dataFormat,
                IsCompressed: isCompressed,
                Color: color,
                MeshId: deviceId + "_" + dataPointId,
                RoadId: road.Id,
                SeriesName: `${device.Title} - ${deviceDataPointViewModel.Title}`,
                SeriesId: `SD-${deviceId.replace(/-/g, "")}${dataPointId.replace(/\./g, "")}`,
                DataPointViewModel: deviceDataPointViewModel
            };

            // if another dataPoint of the same device is active; disable it! 
            // this.dataPoints.filter(dp => dp.DeviceId === activeDataPoint.DeviceId).forEach(dp => this.toggleDataStream(dp.DataPointViewModel));

            this.dataPoints.push(activeDataPoint);

            this.enableDataPoint(activeDataPoint);
        } else {
            this.disableDataPoint(dpIdx);
        }
    }

    public findActiveDataPointIndex(deviceId: string, dataPointId: string) {
        for (var i = 0, len = this.dataPoints.length; i < len; i++) {
            const dp = this.dataPoints[i];
            if (dp.DataPointId === dataPointId && dp.DeviceId === deviceId) {
                return i;
            }
        }

        return -1;
    }

    private findButton(dp: ActiveDataPoint) {
        return $(`div[data-dataPointId='${dp.DataPointId}'][data-deviceId='${dp.DeviceId}']`);
    }

    private enableDataPoint(dp: ActiveDataPoint) {
        $.post("/api/DeviceDataPoint/EnableDeviceDataPoint",
            {
                'DeviceId': dp.DeviceId,
                'DataPointId': dp.DataPointId
            })
            .done(() => {
                this.dataPointEnabled(dp);
            });
    }

    private dataPointEnabled(dp: ActiveDataPoint) {
        let button = this.findButton(dp);
        button.addClass("active");
        button.css("background-color", dp.Color.getStyle());

        this.updateView();

        this.attachedGraphs.forEach(val => val.resize()); // need to resize, because initially the control is hidden and thus the width/height unusable

        TemsWebEvents.publishEvent(TemsWebEvents.DataStreamEnabled, [dp.DataPointViewModel]);
    }

    private isGraphFormat(dataPoint: ActiveDataPoint) {
        return $.inArray(dataPoint.DataFormat, DataStreamManagerObj.graphFormats) >= 0;
    }

    private isImageFormat(dataPoint: ActiveDataPoint) {
        return $.inArray(dataPoint.DataFormat, DataStreamManagerObj.imageFormats) >= 0;
    }

    private any2DViewDataPointsActive(): boolean {
        return this.OnlyGraphDataPoints.length > 0;
    }

    private anyImageDataPointsActive(): boolean {
        return this.OnlyImageDataPoints.length > 0;
    }

    private updateView() {
        // current state
        // const profilesActive = $("#liveData2DControl").is(":visible");
        // const imagesActive = $("#liveImageContainers").is(":visible");

        // should state
        const profilesShouldBeActive = this.any2DViewDataPointsActive();
        const imagesShouldBeActive = this.anyImageDataPointsActive();

        // determine sizes
        const fullSize = profilesShouldBeActive ? !imagesShouldBeActive : imagesShouldBeActive; // poor mans xor (js doesn't have this for logical operations)
        $("#liveData2DControl").toggleClass("col-sm-12", fullSize);
        $("#liveImageContainers").toggleClass("col-sm-12", fullSize);

        $("#liveData2DControl").toggleClass("col-sm-6", !fullSize);
        $("#liveImageContainers").toggleClass("col-sm-6", !fullSize);

        $("#liveData2DControl").toggle(profilesShouldBeActive);
        $("#liveImageContainers").toggle(imagesShouldBeActive);

        if (!profilesShouldBeActive && !imagesShouldBeActive) {
            $("#liveDataContainer").hide("slow");
        } else {
            if (!$("#liveDataContainer").is(":visible")) {
                $("#liveDataContainer").show();
            }
        }

        $("#liveData2DControl").resize();
    }

    private disableDataPoint(dpIdx: number) {
        let dp = this.dataPoints.splice(dpIdx, 1)[0];
        this.colorPalette.push(dp.Color);

        this.updateView();

        $.post("/api/DeviceDataPoint/DisableDeviceDataPoint", {
            'DeviceId': dp.DeviceId,
            'DataPointId': dp.DataPointId
        }).done(() => {
            this.dataPointDisabled(dp);
        });
    }

    private dataPointDisabled(dp: ActiveDataPoint) {
        let button = this.findButton(dp);
        button.removeClass("active");
        button.css("background-color", "white");

        var meshId = `${dp.DeviceId}_${dp.DataPointId}`;
        this.siteView.getScene().removeMeshById(meshId);

        this.attachedGraphs.forEach(graph => graph.removeSeries(dp.SeriesId));
        this.imageWrapPanel.removeItem(dp.DataPointId);
        this.imageWrapPanel.redimensionImages();

        TemsWebEvents.publishEvent(TemsWebEvents.DataStreamDisabled, [dp.DataPointViewModel]);
    }

    private requestDataPoints(reqDataPoints: ActiveDataPoint[], resetTimerCallback: () => void) {
        if (reqDataPoints.length > 0) {
            const maxWidth = $("#liveImageContainers").find(".wrapPanelSingleImage").first().width() || 100;
            const maxHeight = $("#liveImageContainers").find(".wrapPanelSingleImage").first().height() || 100;

            let strippedDp: DeviceDataIdentifier[] = reqDataPoints.map((p) => {
                return {
                    DeviceId: p.DeviceId,
                    DataPointId: p.DataPointId,
                    DataFormat: p.DataFormat,
                    IsCompressed: p.IsCompressed,
                    MaxWidth: maxWidth,
                    MaxHeight: maxHeight
                };
            });

            $.post("/api/DeviceDataPoint/GetDataPoints", {
                DataPoints: strippedDp
            }).done((dataPointsCollection: DeviceDataViewModel[]) => {
                resetTimerCallback();
                if (dataPointsCollection !== null) {
                    $.each(dataPointsCollection, (idx, dpc) => {
                        const dpIdx = this.findActiveDataPointIndex(dpc.DeviceDataIdentifier.DeviceId, dpc.DeviceDataIdentifier.DataPointId);
                        if (dpIdx === -1) {
                            return;
                        }

                        const dp = this.dataPoints[dpIdx];
                        if (dpc.hasOwnProperty("Points")) {
                            this.visualizeDataPoints(dp, dpc as DeviceDataViewModelProfile3D);
                        } else if (dpc.hasOwnProperty("ImageDataUrl")) {
                            this.visualizeDataImage(dp, dpc as DeviceDataViewModelImage);
                        }
                    });
                }
            }).fail(() => {
                resetTimerCallback();
            });
        } else {
            resetTimerCallback();
        }
    }

    private resetGraphDataTimer() {
        setTimeout(() => this.requestDataPoints(this.OnlyGraphDataPoints, () => this.resetGraphDataTimer()), 300);
    }

    private resetImageDataTimer() {
        setTimeout(() => this.requestDataPoints(this.OnlyImageDataPoints, () => this.resetImageDataTimer()), 1000);
    }

    private visualizeDataImage(dp: ActiveDataPoint, data: DeviceDataViewModelImage): void {
        let domImageContainer = this.imageWrapPanel.getItem(data.DeviceDataIdentifier.DataPointId);
        if (domImageContainer === null) {
            domImageContainer = $(`<div>
                                            <label style="margin-bottom: 1em;">${dp.SeriesName}</label>
                                            <div class='wrapPanelSingleImageIeFix'><img class='roundedPadded grayBackground wrapPanelSingleImage'></div>
                                        </div>`);
            this.imageWrapPanel.addItem(data.DeviceDataIdentifier.DataPointId, domImageContainer);
        }

        const domImg = domImageContainer.find("img");
        domImg.attr("src", data.ImageDataUrl);
        domImageContainer.children("div.wrapPanelSingleImageIeFix").toggle(data.ImageDataUrl !== "");

        const domIcon = domImageContainer.children("div.noCameraDataDiv");
        if (data.ImageDataUrl === "") {
            if (domIcon.length !== 1) {
                domImageContainer.append($(`<div class='roundedPadded grayBackground noCameraDataDiv flexGrow1'>
                                                    <span class='iconLarge glyphicon glyphicon-camera'>
                                                        <span class="glyphicon glyphicon-remove listClearMarker grayBackground"></span>
                                                    </span>
                                                </div>`));
            }
        } else {
            domIcon.remove();
        }
    }

    private visualizeDataPoints(dp: ActiveDataPoint, data: DeviceDataViewModelProfile3D): void {
        const meshId = dp.MeshId;
        const roadMesh = this.siteView.getScene().getObject3DFromMeshId(dp.RoadId);

        let mesh = this.siteView.getScene().getObject3DFromMeshId(meshId);
       
        if (mesh === null) {
            

            this.siteView.getScene().addDummyElement(meshId, 0, false, roadMesh);
            mesh = this.siteView.getScene().changeSpecificRenderingMode(meshId, SurfaceMaterial.Points);

            (<THREE.PointCloudMaterial>mesh.material).color = dp.Color;
            (<THREE.PointCloudMaterial>mesh.material).size = 5;
            (<THREE.PointCloudMaterial>mesh.material).sizeAttenuation = false;

            mesh.meshInfo.IsSelectable = false; // we don't want this object to be part of the selectable geometry.
        }

        var matrix = this.GetTransformationTillMesh(dp, roadMesh);
        var pointsRelativeToRoad = data.Points.map(p => new THREE.Vector3(p.XInMeter, p.YInMeter, p.ZInMeter).applyMatrix4(matrix));

        // Graph Update
        this.attachedGraphs.forEach(graph => graph.renderScatterData({
            SeriesId: dp.SeriesId,
            SeriesName: dp.SeriesName,
            Data: pointsRelativeToRoad.filter(p => Math.abs(p.x) < 30 && Math.abs(p.y) < 30 && Math.abs(p.z) < 30),
            Color: dp.Color.getStyle()
        }, ScatterDataMode.Points));

        var newMesh: TemsObject3D = <TemsObject3D>new THREE.PointCloud(this.buildPointCloudBufferGeometry(pointsRelativeToRoad), mesh.PointCloudMaterial);
        newMesh.meshInfo = mesh.meshInfo;
        this.siteView.getScene().exchangeObject(mesh, newMesh);
    }

    private GetTransformationTillMesh(dp: ActiveDataPoint, mesh: THREE.Mesh) {
        var parentMesh: THREE.Object3D = (dp.DataFormat.indexOf("ProfilePolar3D") !== -1)
            ? this.siteView.getScene().getObject3DFromMeshId(dp.DeviceId.replace(/-/g, ""))// TODO this is something that shouldn't be needed. the problem is that we throw guid around with and without dashes
            : mesh;
        var totalMatrix = new THREE.Matrix4();
        while (parentMesh && parentMesh != mesh) {
            var cloneMesh = parentMesh.clone();
            cloneMesh.scale.set(1, 1, 1);
            cloneMesh.updateMatrix();
            totalMatrix = cloneMesh.matrix.multiply(totalMatrix);
            parentMesh = parentMesh.parent;
        }
        return totalMatrix;
    }

    private buildPointCloudBufferGeometry(points: THREE.Vector3[]) {
        var pointsBufferLength = points.length * 3;
        var vertices = Array(points.length);
        var p: THREE.Vector3;
        for (var i = 0, j = 0; i < pointsBufferLength; i += 3, j++) {
            p = points[j];
            vertices[i] = p.x;
            vertices[i + 1] = p.y;
            vertices[i + 2] = p.z;
        }

        var geometry = new THREE.BufferGeometry();
        geometry.addAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
        return geometry;
    }
}

